/*
 * The all-blocks overlay (views/partials/all-blocks-overlay.njk): one Snap!
 * project's every custom-block definition and hatted script at one zoom,
 * wrapping across the viewport (modules/snap-pics-layout.ts, the
 * per-student snap-blocks page's layout). Opened from a snap-grid card's
 * "all" link (snap-grid.ts) and the Snap! coding page's "All blocks" link
 * (snap.ts). The opener says who the project belongs to and supplies the
 * pics; the overlay owns the zoom (which persists across opens), the close
 * gestures, and the rendering/empty/error states.
 */

import { errorDetail } from './fetch-helpers.ts';
import { layoutPics, libraryNote } from './snap-pics-layout.ts';
import type { DefinitionPic } from './snap-render.ts';

// What an opener's loader resolves to: the pics (with how many library
// blocks were left out, which the header reports), or a note in their
// place — `empty` in the quiet style ("no save yet"), `error` in red.
export type OverlayContent =
  | { defs: DefinitionPic[]; libraryBlocks: number }
  | { empty: string }
  | { error: string };

export interface AllBlocksOverlay {
  /*
   * Show the overlay for `who`'s project, "rendering…" until `load`
   * resolves. `codeUrl` is the project on its coding page (the header's
   * "code" link), or null to show none. A loader that throws shows as a
   * failed load. Opening again before the load lands supersedes it.
   */
  open(who: string, codeUrl: string | null, load: () => Promise<OverlayContent>): Promise<void>;
}

const note = (text: string): HTMLElement => {
  const div = document.createElement('div');
  div.className = 'empty-state';
  div.textContent = text;
  return div;
};

export const allBlocksOverlay = (overlay: HTMLDialogElement): AllBlocksOverlay => {
  const whoEl = overlay.querySelector('.who') as HTMLElement;
  const codeEl = overlay.querySelector('.code') as HTMLAnchorElement;
  const blocksEl = overlay.querySelector('.blocks') as HTMLElement;
  const noteEl = overlay.querySelector('.library-note') as HTMLElement;
  const zoom = overlay.querySelector('select') as HTMLSelectElement;
  const close = overlay.querySelector('button.close') as HTMLButtonElement;

  let layout: { rezoom(zoom: number): void } | null = null;
  let opens = 0;

  zoom.onchange = () => layout?.rezoom(Number(zoom.value));
  close.onclick = () => overlay.close();
  // Clicking the backdrop closes; clicks inside the panel don't.
  overlay.onclick = (e) => {
    if (e.target === overlay) overlay.close();
  };

  return {
    async open(who, codeUrl, load) {
      const seq = ++opens;
      whoEl.textContent = who;
      codeEl.hidden = !codeUrl;
      codeEl.href = codeUrl ?? '';
      overlay.classList.remove('errored');
      noteEl.textContent = '';
      blocksEl.replaceChildren(note('rendering…'));
      layout = null;
      overlay.showModal();
      let content: OverlayContent;
      try {
        content = await load();
      } catch (e) {
        console.error('all-blocks overlay: rendering failed:', e);
        content = { error: `project failed to load: ${errorDetail(e)}` };
      }
      if (!overlay.open || seq !== opens) return; // closed or moved on
      if ('defs' in content) {
        noteEl.textContent = libraryNote(content.libraryBlocks);
        layout = layoutPics(blocksEl, content.defs, Number(zoom.value), content.libraryBlocks);
      } else if ('empty' in content) {
        blocksEl.replaceChildren(note(content.empty));
      } else {
        overlay.classList.add('errored');
        blocksEl.replaceChildren(note(content.error));
      }
    },
  };
};
